import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import { TrackingContext } from '@vrbo/react-event-tracking';
import { retrieveData } from '../../../../actions/graphQlSvc';
import TranslationUtil from '../../../../utils/TranslationUtil';
import { createPageViewEvent } from '../../../../utils/EventProviders/EgClickstreamUtil';
import LeadActions from '../../../../actions/lead';
import log from '../../../../utils/ClientLogger';
import ErrorPage from './ErrorPage';
import SuccessPage from './SuccessPage';
import VerifiedPage from './VerifiedPage';
import withPageFlow from '../../../PageFlow/PageFlow';

export const EMAIL_RESPONSE_DECRYPT = gql`
  query DecryptUserInformation($information: String!) {
    decryptUserInformation(information: $information) {
      propertyId
      userId
      verificationStatus
    }
  }
`;

export const IsEmailVerified = ({
    successPage,
    referrer,
    errorPage,
    expiredPage,
    verifiedPage,
    setPropertyId,
    setUserId,
    pageFlow
}) => {
    const { trigger } = useContext(TrackingContext);

    const [data, setData] = useState();
    const [ready, setReady] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState();

    useEffect(() => {
        window.scrollTo(0, 0);
        trigger('pageLoad', {
            pageName: 'partner-qualifier: is-email-verified',
            referrer
        });
        trigger('pageview', createPageViewEvent('is_email_verified'));
        trigger('pageLoadGtm', {
            pageName: 'IsEmailVerified',
            event: 'IsEmailVerifiedLoad'
        });
        const params = new URLSearchParams(window.location.search);
        const dataParam = params.get('data');
        if (!dataParam) setReady(true);
        setData(dataParam);
        return () => {
            trigger('pageUnload', { referrer });
        };
    }, []);

    useEffect(() => {
        const decryptData = async () => {
            try {
                const { error: queryError, data: queryData } = await retrieveData(
                    EMAIL_RESPONSE_DECRYPT,
                    {
                        information: data
                    }
                );
                if (queryError) throw new Error(queryError);
                const { decryptUserInformation } = queryData;
                const {
                    propertyId,
                    userId,
                    verificationStatus: status
                } = decryptUserInformation;
                setPropertyId(propertyId);
                setUserId(userId);
                setVerificationStatus(status);
            } catch (e) {
                log('error', 'Error decrypting user information', {
                    error: e
                });
                setVerificationStatus('ERROR');
            }
            setReady(true);
        };
        if (data) {
            decryptData();
        }
    }, [data]);

    if (!ready) return null;

    switch (verificationStatus) {
    case 'VERIFIED':
        return <SuccessPage {...{ ...successPage, pageFlow }} />;
    case 'EXPIRED':
        return (
            <ErrorPage {...{ ...expiredPage, reason: verificationStatus }} />
        );
    case 'NOT_FOUND':
        return <VerifiedPage {...{ ...verifiedPage, reason: verificationStatus, pageFlow }} />;
    case 'ERROR':
    case 'INVALID':
    case 'VALID':
        return <ErrorPage {...{ ...errorPage, reason: verificationStatus }} />;
    default:
        return <SuccessPage {...{ ...successPage, pageFlow }} />;
    }
};

IsEmailVerified.propTypes = {
    referrer: PropTypes.string.isRequired,
    setPropertyId: PropTypes.func.isRequired,
    setUserId: PropTypes.func.isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func
    }).isRequired,
    successPage: PropTypes.shape({
        title: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        cta: PropTypes.string.isRequired,
        pageFlow: PropTypes.shape({
            next: PropTypes.func
        }).isRequired
    }).isRequired,
    errorPage: PropTypes.shape({
        title: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        cta: PropTypes.string.isRequired,
        sentText: PropTypes.string.isRequired,
        propertyId: PropTypes.string.isRequired
    }).isRequired,
    expiredPage: PropTypes.shape({
        title: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        cta: PropTypes.string.isRequired,
        sentText: PropTypes.string.isRequired,
        propertyId: PropTypes.string.isRequired
    }).isRequired,
    verifiedPage: PropTypes.shape({
        title: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        cta: PropTypes.string.isRequired,
        pageFlow: PropTypes.shape({
            next: PropTypes.func
        }).isRequired
    }).isRequired
};

/* istanbul ignore next */
export const mapStateToProps = (state) => {
    const { app, lead } = state;

    const translations = new TranslationUtil(
        app.translations,
        app.fallbackTranslations
    );
    const { referrer } = app;

    return {
        referrer,
        successPage: {
            title: translations.getTranslation('emailVerification.result.title'),
            text: translations.getTranslation('emailVerification.result.text'),
            cta: translations.getTranslation('emailVerification.result.cta')
        },
        errorPage: {
            title: translations.getTranslation('emailVerification.error.title'),
            text: translations.getTranslation('emailVerification.error.text'),
            cta: translations.getTranslation('emailVerification.error.cta'),
            sentText: translations.getTranslation('emailVerification.error.sentText'),
            propertyId: lead.propertyId
        },
        expiredPage: {
            title: translations.getTranslation('emailVerification.expired.title'),
            text: translations.getTranslation('emailVerification.expired.subtitle'),
            cta: translations.getTranslation('emailVerification.expired.cta'),
            sentText: translations.getTranslation('emailVerification.error.sentText'),
            propertyId: lead.propertyId
        },
        verifiedPage: {
            title: translations.getTranslation(
                'emailVerification.alreadyVerified.title'
            ),
            text: translations.getTranslation(
                'emailVerification.alreadyVerified.subtitle'
            ),
            cta: translations.getTranslation('emailVerification.result.cta')
        }
    };
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
    setPropertyId: propertyId => dispatch(LeadActions.setPropertyId(propertyId)),
    setUserId: userId => dispatch(LeadActions.setUserId(userId))
});

const IsEmailVerifiedCL = connect(
    mapStateToProps,
    mapDispatchToProps
)(IsEmailVerified);
IsEmailVerifiedCL.displayName = 'ClIsEmailVerified';
export default hot(module)(withPageFlow(IsEmailVerifiedCL));
